/**
 * Group D — posts, comments and labels (`docs/api-v1/04-v1-surface.md` §3.1 D).
 *
 *     GET /v1/communities/{skool_id}/posts             cursor-paginated
 *     GET /v1/communities/{skool_id}/posts/{post_id}   the body + the thread
 *     GET /v1/communities/{skool_id}/comments          cursor-paginated
 *     GET /v1/communities/{skool_id}/labels            the feed-category vocabulary
 *     PATCH /v1/communities/{skool_id}/labels/{label_id}   name one by hand
 *
 * TWO THINGS DIFFER FROM THE LEGACY TWINS AND BOTH ARE DELIBERATE.
 *
 * 1. PAGINATION IS CURSOR-BASED. `?offset=` is a 400 here (`invalid_pagination`):
 *    §1.3 licenses exactly three offset routes and posts/comments are not among
 *    them. A prev/next UI therefore keeps a small stack of cursors — the cursor for
 *    page N is what page N−1 handed back — which is what `CursorStack` below is for.
 *    `total` is still returned, so "21–40 of 313" survives.
 *
 * 2. A POST DETAIL IS THE RESOURCE, NOT `{post, comments}`. The thread is
 *    `PostItem.comments` (§2.2) and `?include=comments` is on by default, because the
 *    thread is what the route is for. `fetchPostThread` at the bottom folds that into
 *    the `{post, comments}` every reading drawer on both surfaces takes — one
 *    conversion, in the layer that knows the wire.
 *
 * `content` is NOT in the list route's default field set and `excerpt` is — grepping
 * all of `web-app/src` finds no reader of a list row's body (§5.4). The detail route
 * inverts that: `content` is default-on there.
 */

import { apiGet, apiPatch } from './http';
import { community } from './envelope';
import type { CommunityRef, ListEnvelope, QueryParams } from './envelope';

import type { CommentItem, ImageMedia, PostAuthor, PostDetail, VideoMedia } from './shapes';


// ---------------------------------------------------------------------------
// §2.2 — the one post and comment representation
// ---------------------------------------------------------------------------

export interface Author {
  member_id: number;
  external_id: string;
  handle: string | null;
  name: string;
  picture_profile: string | null;
}

export interface PostItem {
  kind: 'post';
  id: number;
  external_id: string;
  title: string | null;
  slug: string | null;
  url: string | null;
  author: Author | null;
  pinned: boolean;
  upvotes: number | null;
  comment_count: number | null;
  created_at: string | null;
  /**
   * Deleted on Skool — the id 404s there now. Null for live content.
   *
   * Only the Helper can ever discover this: the dashboard holds an admin session and
   * no Skool session, so it cannot ask. It arrives via the `deleted` ingest source and
   * is served from the columns that already recorded it
   * (`posts.comments_unavailable_at` / `comments.deleted_at`).
   *
   * A deleted row KEEPS BEING SERVED in the queue — that is the product decision in
   * #201. Render the badge; do not filter. And treat it as reversible: the reconciler
   * clears a comment's flag if a later complete pass returns it.
   */
  deleted_at?: string | null;
  /** Default-on for the list, absent on the detail route (which sends `content`). */
  excerpt?: string | null;
  /** Default-on for the detail route only. */
  content?: string | null;
  label_id?: string | null;
  images?: ImageMedia[] | null;
  videos?: VideoMedia[] | null;
  updated_at?: string | null;
  last_comment_at?: string | null;
  /** `?include=comments` — flat and chronological, each carrying `parent_id`/`depth`
   *  so the client indents. Present by default on the detail route. */
  comments?: CommentRow[];
}

export interface CommentRow {
  kind: 'comment';
  id: number;
  external_id: string;
  /** Always present in v1, where the legacy thread left it implicit in the URL. */
  post_id: number;
  parent_id: number | null;
  depth: number;
  pinned: boolean;
  author: Author | null;
  upvotes: number | null;
  created_at: string | null;
  /** The `?p=<first 8 chars>` deep link straight to this comment. */
  url: string | null;
  content: string | null;
  images?: ImageMedia[] | null;
  videos?: VideoMedia[] | null;
  /** `?include=post` — `{id, title, slug, url}`, off by default. */
  post?: { id: number; title: string | null; slug: string | null; url: string | null };
  /**
   * Direct replies to this comment. Absent from a route that does not count them;
   * a route that counts and finds none sends 0, so `?? null` — never `?? 0` — is the
   * right way to read it.
   */
  reply_count?: number | null;
}

export interface Label {
  label_id: string;
  name: string | null;
  source: string | null;
  posts: number;
}

export interface PostPage extends ListEnvelope<PostItem> {
  community: CommunityRef;
}
export interface CommentPage extends ListEnvelope<CommentRow> {
  community: CommunityRef;
}
export interface LabelPage extends ListEnvelope<Label> {
  community: CommunityRef;
}

export type PostSort = 'new' | 'comments' | 'upvotes';

// ---------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------

export interface PostQuery {
  limit?: number;
  cursor?: string | null;
  q?: string;
  memberId?: number | null;
  /** Repeated on the wire (`?label_id=a&label_id=b`), never comma-joined — the
   *  legacy twin splits one scalar on commas, which is the shape that produced
   *  `categories=""` meaning "show none" (§1.6). */
  labelIds?: string[];
  unanswered?: boolean;
  sort?: PostSort;
  /** UTC instants. `since` is inclusive, `until` EXCLUSIVE, on every v1 route. */
  since?: string | null;
  until?: string | null;
}

function postParams(opts: PostQuery): QueryParams {
  return {
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
    q: opts.q?.trim() || undefined,
    member_id: opts.memberId ?? undefined,
    label_id: opts.labelIds?.length ? opts.labelIds : undefined,
    unanswered: opts.unanswered ? true : undefined,
    sort: opts.sort && opts.sort !== 'new' ? opts.sort : undefined,
    since: opts.since ?? undefined,
    until: opts.until ?? undefined,
  };
}

export function fetchPosts(skoolId: number, opts: PostQuery = {}): Promise<PostPage> {
  return apiGet<PostPage>(community(skoolId, '/posts'), postParams(opts));
}

/** One post with its body and its thread. A post in another community is a 400
 *  `item_not_in_community`, not a 404 — the caller holds this community and asked
 *  the wrong question about it. */
export function fetchPost(skoolId: number, postId: number): Promise<PostItem> {
  return apiGet<PostItem>(community(skoolId, `/posts/${postId}`));
}

export interface CommentQuery {
  limit?: number;
  cursor?: string | null;
  q?: string;
  memberId?: number | null;
  /** `?include=post` — the parent's `{id, title, slug, url}`. Off by default: only
   *  the member page's comment column renders the parent title. */
  withPost?: boolean;
  since?: string | null;
  until?: string | null;
}

export function fetchComments(skoolId: number, opts: CommentQuery = {}): Promise<CommentPage> {
  return apiGet<CommentPage>(community(skoolId, '/comments'), {
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
    q: opts.q?.trim() || undefined,
    member_id: opts.memberId ?? undefined,
    since: opts.since ?? undefined,
    until: opts.until ?? undefined,
    ...(opts.withPost ? { include: ['post'] } : {}),
  });
}

/** The feed-category vocabulary. Paginated, unlike its legacy twin — the timeline
 *  wants the whole (small) set for its filter chips, so it asks for the maximum page
 *  and does not walk: a community with more than 200 distinct Skool categories does
 *  not exist, and if one appears the chips simply show the 200 most-used. */
export function fetchLabels(skoolId: number, limit = 200): Promise<LabelPage> {
  return apiGet<LabelPage>(community(skoolId, '/labels'), { limit });
}

/**
 * `PATCH /v1/communities/{skool_id}/labels/{label_id}` — name a feed label by hand.
 * Manual names win over scraped ones and are never clobbered by the scraper.
 *
 * THREE CHANGES FROM `POST /admin/labels/set-name`:
 *   * it is a PATCH on the label, so `skool_id` and `label_id` are path segments
 *     rather than body fields the server has to re-derive scope from (§1.1);
 *   * it RETURNS THE LABEL — `{label_id, name, source, posts}`, the same row
 *     `fetchLabels` emits — where the legacy route answered `{status: "ok"}`. The
 *     caller patches its row from the response instead of assuming what it wrote
 *     (W9); `source` comes back `"manual"`, which is the bit the table renders as
 *     "renamed by you";
 *   * a label id no post in this community carries is `404 label_not_found` rather
 *     than a silently created orphan row, and a blank name is `422 validation_failed`
 *     (the table already guards on empty, so nothing reaches that).
 *
 * `label_id` is an opaque Skool string and is encoded — it is not ours to assume the
 * character set of.
 */
export function renameLabel(
  skoolId: number,
  labelId: string,
  name: string,
): Promise<Label> {
  return apiPatch<Label>(
    community(skoolId, `/labels/${encodeURIComponent(labelId)}`),
    { name },
  );
}

// ---------------------------------------------------------------------------
// Adapters to the shared components
// ---------------------------------------------------------------------------

/** `handle` is `string | null` on the wire and `string` on the legacy `PostAuthor`
 *  every card component takes. Every render site guards on truthiness
 *  (`author?.name || author?.handle || 'Unknown'`), so the empty string is the
 *  honest fill — it renders as absent, where `'unknown'` would render as a handle. */
export function toAuthor(a: Author | null | undefined): PostAuthor | null {
  return a ? { ...a, handle: a.handle ?? '' } : null;
}

function toComment(c: CommentRow): CommentItem {
  return {
    id: c.id,
    external_id: c.external_id,
    parent_id: c.parent_id,
    depth: c.depth,
    pinned: c.pinned,
    author: toAuthor(c.author),
    content: c.content,
    images: c.images ?? null,
    videos: c.videos ?? null,
    upvotes: c.upvotes,
    created_at: c.created_at,
    url: c.url,
    reply_count: c.reply_count ?? null,
  };
}


// ---------------------------------------------------------------------------
// Cursor paging for a prev/next UI
// ---------------------------------------------------------------------------

/**
 * The cursors that lead to each page, so a cursor-paginated list can still offer
 * prev/next and "21–40 of 313".
 *
 * A cursor is opaque and one-directional: the server hands back the cursor for the
 * NEXT page and there is no "previous" cursor to ask for. Keeping the ones already
 * seen is the whole mechanism — entry 0 is null (page 1 asks for no cursor), and
 * going back is reading an entry we already hold rather than a new kind of request.
 * `CURSOR_START` is that first entry; a filter change resets the list to it, which is
 * also what the server requires — a cursor carries a hash of the filters it was
 * minted under and answers 400 `cursor_filter_mismatch` if they have moved (§1.3).
 *
 * A NEW array comes back rather than a mutation: the callers hold this in `$state`,
 * and an in-place `push` on a plain array would not re-run their `$derived`.
 */
export const CURSOR_START: (string | null)[] = [null];

export function withNextCursor(
  cursors: (string | null)[],
  index: number,
  next: string | null,
): (string | null)[] {
  const kept = cursors.slice(0, index + 1);
  // No `next_cursor` means this is the last page — truncating is what makes
  // "is there another page?" a property of the list rather than a second request.
  return next ? [...kept, next] : kept;
}


// One post with its whole thread — `{post, comments}`, which is what every reading
// drawer on both surfaces takes. The route returns the post FLAT with its comments
// hanging off it; this is the one conversion, in the layer that knows the wire.
export async function fetchPostThread(skoolId: number, postId: number): Promise<PostDetail> {
  const item = await apiGet<PostItem>(community(skoolId, `/posts/${postId}`));
  return {
    post: {
      id: item.id,
      external_id: item.external_id,
      title: item.title,
      slug: item.slug,
      content: item.content ?? null,
      author: toAuthor(item.author),
      images: item.images ?? null,
      videos: item.videos ?? null,
      comment_count: item.comment_count,
      upvotes: item.upvotes,
      pinned: item.pinned,
      created_at: item.created_at,
      updated_at: item.updated_at ?? null,
      url: item.url,
    },
    comments: (item.comments ?? []).map(toComment),
  };
}
